import { IonCard, IonCardContent, IonImg, IonText } from '@ionic/react';
import { useHistory } from 'react-router';

import { Icon } from '@/components/Icon';
import { IconRotate } from '@/components/Icon/types';
import { CustomLoading } from '@/components/Loading/CustomLoading';

import { getTitleAndDescriptionFromInfo } from '@/utils/getTitleAndDescriptionFromReportData';

import Running from '@/assets/icons/Running.png';
import SLSquatter from '@/assets/icons/SingleLegSquat.png';
import Squatter from '@/assets/icons/Squat.png';
import Walking from '@/assets/icons/Walking.png';
import ReportCardPattern from '@/assets/report-card-pattern.svg';

import { ReportStatus, ReportType } from '@/models';

import styles from './ReportCard.module.css';

interface ReportCardProps {
  id: string;
  name: string;
  date: Date;
  reportType: ReportType;
  majorVersion: number;
  status?: ReportStatus;
}

export const ReportCard = ({ id, name, date, reportType, majorVersion, status }: ReportCardProps) => {
  const pending = status === ReportStatus.PENDING;
  const history = useHistory();
  const { title, description } = getTitleAndDescriptionFromInfo(name, date);

  const imageMap = new Map([
    [ReportType.WALK, Walking],
    [ReportType.RUN, Running],
    [ReportType.SQUAT, Squatter],
    [ReportType.SLSQUAT, SLSquatter],
  ]);

  const activityNamesMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'Single Leg Squat'],
  ]);

  const routeSegmentMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'SingleLegSquat'],
  ]);

  const handleClick = () => {
    if (routeSegmentMap.has(reportType)) {
      history.push(`/reportsV2/${routeSegmentMap.get(reportType)}/${id}`, {
        title,
        description,
      });
    }
  };

  return (
    <IonCard className={styles.card} onClick={!pending ? handleClick : undefined}>
      {pending ? (
        <div className={styles.pending}>
          <CustomLoading message={'Creating report...'} size='small' />
        </div>
      ) : (
        <>
          <div className={styles.color}>
            <IonImg className={styles.icon} src={imageMap.get(reportType) ?? Walking} />

            <IonImg className={styles.pattern} src={ReportCardPattern} />
          </div>

          <IonCardContent className={styles.content}>
            <div className={styles.header}>
              <IonText className={styles.name}>{activityNamesMap.get(reportType)}</IonText>
              <IonText class={styles.date}>
                <span className={styles.dateString}>
                  {date.toLocaleDateString(undefined, {
                    month: 'short',
                    year: 'numeric',
                    day: 'numeric',
                  })}
                </span>
                {date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </IonText>
            </div>

            <div className={styles.bottom}>
              <IonText className={styles.activity}>{name}</IonText>
              <Icon className={styles.chevron} color='#829696' direction={IconRotate.East} name='chevron' />
            </div>
          </IonCardContent>
        </>
      )}
    </IonCard>
  );
};
